/**
 * Sentry MUST be initialized before any other imports
 * This ensures error tracking captures initialization errors
 */
import { initSentry } from "@/modules/shared/utils/sentry";

initSentry();

import "@/localization/i18n";

import "./modules/shared/styles/index.css";
import "react-day-picker/style.css";

import { StrictMode } from "react";

import {
  MutationCache,
  QueryCache,
  QueryClient,
  QueryClientProvider,
} from "@tanstack/react-query";
import { createRouter, RouterProvider } from "@tanstack/react-router";
import ReactDOM from "react-dom/client";

import { getMode, MODE } from "@/modules/shared/utils/env-mode";
import {
  enableFormTracking,
  initMixpanel,
} from "@/modules/shared/utils/mixpanel";
import { initPostHog } from "@/modules/shared/utils/posthog";
import { captureReactQueryError } from "@/modules/shared/utils/reactQueryErrorTracking";
import { captureException, ErrorBoundary } from "@/modules/shared/utils/sentry";
import { initSessionReplay } from "@/modules/shared/utils/sessionReplay";

import { ErrorBoundaryFallbackScreen } from "./modules/shared/screens/ErrorBoundaryFallbackScreen";
import { NotFoundScreen } from "./modules/shared/screens/NotFoundScreen";
import { routeTree } from "./routeTree.gen";

initMixpanel();
initPostHog();
initSessionReplay().catch((error) => {
  if (error instanceof Error) {
    captureException(error);
  }
});

if (getMode() !== MODE.PRODUCTION) {
  window.__triggerSentryBridge = () => {
    console.log(
      [
        "[Sentry Bridge] posthog.sentryIntegration() would attach the PostHog replay URL to this Sentry event",
        "  format:  <posthog-host>/project/<projectId>/replay/<sessionId>",
        "  source:  posthog-js Sentry integration registered in initSentry()",
        "  active:  production only (Sentry + PostHog are disabled in dev)",
      ].join("\n"),
    );
    captureException(new Error("Sentry bridge test"));
  };
}

/**
 * Enable automatic form tracking
 *
 * All form submissions will be automatically tracked with metadata
 * including form ID, name, action, method, and field count.
 */
enableFormTracking();

const queryClient = new QueryClient({
  queryCache: new QueryCache({
    onError: (error, query) => {
      captureReactQueryError({
        error,
        hasQueryData: query.state.data !== undefined,
        context: {
          source: "react-query",
          type: "query",
          queryKey: query.queryKey,
        },
      });
    },
  }),
  mutationCache: new MutationCache({
    onError: (error, _variables, _context, mutation) => {
      captureReactQueryError({
        error,
        context: {
          source: "react-query",
          type: "mutation",
          mutationKey: mutation.options.mutationKey,
        },
      });
    },
  }),
});

// Create a new router instance
const router = createRouter({
  routeTree,
  context: {
    queryClient,
  },
  defaultPreload: "intent",
  defaultPreloadStaleTime: 0,
  // defaultErrorComponent: ErrorDisplay,
  defaultNotFoundComponent: NotFoundScreen,
  // defaultPendingComponent: PendingComponent,
});

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}

// eslint-disable-next-line react-refresh/only-export-components
const SentryTestTrigger = () => {
  if (
    import.meta.env.MODE !== MODE.PRODUCTION &&
    window.location.search.includes("sentry-test=1")
  ) {
    throw new Error("Sentry ErrorBoundary trigger");
  }
  return null;
};

const rootElement = document.getElementById("root")!;
if (!rootElement.innerHTML) {
  const root = ReactDOM.createRoot(rootElement);
  root.render(
    <StrictMode>
      <QueryClientProvider client={queryClient}>
        <ErrorBoundary
          fallback={({ resetError }) => (
            <ErrorBoundaryFallbackScreen resetError={resetError} />
          )}
        >
          <SentryTestTrigger />
          <RouterProvider router={router} />
        </ErrorBoundary>
      </QueryClientProvider>
    </StrictMode>,
  );
}
